import { AboutPageMapTooltip } from '@/components/about/tooltip';
import { AnnotatedType } from '@/components/annotated-type';
import { Label } from '@/components/label';
import { SeoHead } from '@/components/seo-head';
import { WorldMap } from '@/components/world-map/svg';
import countryCoordinates from '@/data/country-coordinates.json';
import countryDescriptives from '@/data/country-descriptives.json';
import { getAbsoluteUrl } from '@/utils/get-absolute-url';
import { useLayout } from '@/utils/use-layout';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { useSessionStorage } from '@/utils/use-session-storage';
import { t, Trans } from '@lingui/macro';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { visuallyHidden } from '../styles/predefined';
import { Sidebar } from '@/components/about/sidebar';
import { styled } from '@/styles/stitches.config';
import { loadMessages } from '@/utils/i18n';
import { TooltipProvider } from '@/components/about/tooltipContext';
import { Points } from '@/components/about/points';
import { useLingui } from '@lingui/react';

export type CountryData = (typeof countryCoordinates)[number] & {
  id: CountryIsoCode;
  data: CountryStats;
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const baseUrl = getAbsoluteUrl(ctx.req);
  const messages = await loadMessages(ctx.locale);

  const totalRespondents = Object.entries(countryDescriptives).reduce(
    (acc, [_, data]) =>
      acc + data.respondents_2023 + (data.respondents_2021 ? data.respondents_2021 : 0),
    0
  );

  const totalCountries = Object.keys(countryDescriptives).length;

  const countryData = countryCoordinates.map((el) => ({
    ...el,
    data: countryDescriptives[el.id as keyof typeof countryDescriptives],
  }));

  return {
    props: {
      messages,
      baseUrl,
      countryData: countryData as CountryData[],
      totalCountries,
      totalRespondents,
    },
  };
}

const MapContainer = styled('section', {
  position: 'relative',
  height: '100vmin',
  '@bp11': {
    height: '30rem',
  },
});

const MapOverlay = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
});

const MaxWidth = styled('div', {
  paddingInline: '$4',
  maxWidth: '75rem',
  marginInline: 'auto',

  '@bp11': {
    paddingInline: '$6',
  },
});

const MapOverlayContent = styled(MaxWidth, {
  display: 'grid',
  alignItems: 'end',
  minHeight: '100%',

  '@bp11': {
    alignItems: 'center',
  },
});

const SurveyStats = styled('dl', {
  display: 'flex',
  flexDirection: 'row-reverse',
  justifyContent: 'flex-end',
  gap: '$4',
  listStyleType: 'none',
  color: '$white',

  '@bp11': {
    flexDirection: 'column',
    gap: 0,
  },
});

const StatWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  '@bp11': {
    marginBlockEnd: '$4',
  },
});

const Dt = styled('dt', {
  maxWidth: '10rem',
  lineHeight: '$1',
});

const Dd = styled('dd', {
  marginInlineStart: 0,
  fontSize: '$5',
  fontWeight: '$bold',
});

const PageContent = styled('section', {
  backgroundColor: '$white',
});

const PageContentLayout = styled(MaxWidth, {
  display: 'grid',
  gridTemplateColumns: '1fr',
  paddingBlock: '$6',
  paddingInline: '$4',

  '@bp11': {
    gridTemplateColumns: '2fr 1fr',
    paddingBlock: '$9',
  },
});

const H2 = styled('h2', {
  color: '$unicef',
  fontSize: '$3',
  marginBlockStart: '$6',
  marginBlockEnd: '$3',
});

const A = styled('a', {
  color: '$unicef',

  '&:hover': {
    color: '$black',
  },

  '&::focus-visible, &.focus-visible': {
    color: '$black',
    outlineOffset: 3,
    outlineColor: '$unicef',
    outlineWidth: 'thick',
    outlineStyle: 'dotted',
  },
});

const VisuallyHidden = styled('span', visuallyHidden);

const getId = (d: CountryData) => d.id;

// This page will contain the about page
export default function AboutPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { baseUrl, countryData, totalCountries, totalRespondents } = props;

  // if folks land on the about page, don't show them the SurveyBackground component on story pages
  const [_, setShowSurveyBackground] = useSessionStorage('showSurveyBackground', false);
  useEffect((): void => setShowSurveyBackground(false), [setShowSurveyBackground]);

  const formatDecimal = useNumberFormatting('decimal');

  useLayout('blue');
  useLingui();

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={t({
          message: `About The Changing Childhood Project`,
          id: 'about-page.page-title',
        })}
      />

      <MapContainer>
        <TooltipProvider getId={getId}>
          <WorldMap offset={-0.2}>
            <Points
              countryData={countryData}
              getTooltip={(context) => (
                <AboutPageMapTooltip
                  getItems={(d): [string, number | undefined][] => [
                    [
                      t({ id: 'about.tooltip.2023-respondents', message: '2023 Respondents' }),
                      d.data.respondents_2023,
                    ],
                    [
                      t({ id: 'about.tooltip.2021-respondents', message: '2021 Respondents' }),
                      d.data.respondents_2021,
                    ],
                  ]}
                  {...context}
                />
              )}
            />
          </WorldMap>
        </TooltipProvider>

        <MapOverlay>
          <MapOverlayContent>
            <VisuallyHidden as="h3">
              <Trans id="about.statistics.hidden-title">
                Statistics about the UNICEF OGIP survey
              </Trans>
            </VisuallyHidden>

            <SurveyStats>
              <StatWrapper>
                <Dt>
                  <Label>
                    <Trans id="about.participating-countries">Participating Countries</Trans>
                  </Label>
                </Dt>
                <Dd>{formatDecimal(totalCountries)}</Dd>
              </StatWrapper>
              <StatWrapper>
                <Dt>
                  <Label>
                    <Trans id="about.total-respondents">Total Respondents</Trans>
                  </Label>
                </Dt>
                <Dd>{formatDecimal(totalRespondents)}</Dd>
              </StatWrapper>
            </SurveyStats>
          </MapOverlayContent>
        </MapOverlay>
      </MapContainer>

      <PageContent>
        <PageContentLayout>
          <MainContent />
          <Sidebar />
        </PageContentLayout>
      </PageContent>
    </>
  );
}

const MainContentRoot = styled('div', {
  marginBlockEnd: '$6',

  '@bp11': {
    paddingInlineEnd: '$7',
  },
});

const H1 = styled('h1', {
  color: '$unicef',
  marginBlockEnd: '$6',
  lineHeight: '$3',
  '[lang=ta] &': {
    fontSize: '1.5em',
  },
});

const Tagline = styled('p', {
  color: '$unicef',
  fontSize: '$3',
  fontWeight: '$bold',
});

function MainContent() {
  const { locale } = useRouter();

  const methodology2021Url = useMemo(() => {
    switch (locale) {
      case 'es':
        return 'https://www.unicef.org/globalinsight/media/2251/file';
      case 'fr':
        return 'https://www.unicef.org/globalinsight/media/2256/file';
      case 'ar':
        return 'https://www.unicef.org/globalinsight/media/2261/file';
      case 'en':
      default:
        return 'https://www.unicef.org/globalinsight/media/2226/file';
    }
  }, [locale]);

  const methodology2023Url = 'https://www.unicef.org/globalinsight/media/3236/file';

  const report2023Url = 'https://www.unicef.org/globalinsight/media/3311/file';

  return (
    <MainContentRoot>
      <p>
        <Label css={{ color: '$unicef' }}>
          <Trans id="about.attribution-2023">A UNICEF microsite and report series</Trans>
        </Label>
      </p>

      <H1>
        <Trans id="about.title">
          About the{' '}
          <AnnotatedType css={{ backgroundColor: '$unicef' }}>Changing Childhood</AnnotatedType>{' '}
          Project
        </Trans>
      </H1>

      <Tagline>
        <Trans id="about.tagline">Motivation</Trans>
      </Tagline>

      <p>
        <Trans id="about.motivation">
          We are living through an era of rapid and far-reaching transformation. As the world has
          changed — becoming more digital, more globalized, and more diverse — childhood is changing
          with it. The Changing Childhood Project — a collaboration of UNICEF and Gallup — was
          created to explore these shifts, and to better understand what it means to be a child in
          the 21st century. The project seeks to answer two questions: What is it like growing up
          today? And how do young people see the world differently? To answer these questions, we
          wanted to hear from children and young people themselves. Comparing the experiences and
          views of young versus older people offers a powerful lens to explore how childhood is
          changing, and where generations diverge or converge. The ultimate goal of the project is
          to centre young people — their experiences and perspectives — in the work of improving
          life for all children, today and into the future.
        </Trans>
      </p>

      <H2>
        <Trans id="about.microsite-title">The microsite</Trans>
      </H2>

      <p>
        <Trans id="about.microsite">
          This microsite was commissioned by UNICEF to enable people from around the world,
          especially children and young people, to engage with the Project's questions and explore
          its findings. We hope you enjoy it! The microsite was developed by the design firm,
          CLEVER°FRANKE. No data about users are collected through this microsite.
        </Trans>
      </p>
      <p>
        <Trans id="about.microsite-2">
          The results presented in this microsite draw directly from the survey data outlined
          below.* All references to an overall figure or average—e.g., “on average” or “an average
          of”—refer to the survey results for a given question for the median country across all
          countries surveyed that year. Similarly, the “average” for a given country income group is
          derived from survey results from the median country in that group of countries.
        </Trans>
      </p>

      <H2>
        <Trans id="about.survey-title-2023">The surveys</Trans>
      </H2>

      <p>
        <Trans id="about.survey-1">
          To explore the Project's questions and themes, UNICEF and Gallup have partnered on two
          multi-generational, international surveys.
        </Trans>
      </p>

      <p>
        <Trans id="about.survey-2">
          Microstories marked “NEW” reflect data drawn from UNICEF’s work with the{' '}
          <A href={report2023Url}>latest</A> World Poll. All others are based on data drawn from the
          2021 UNICEF-Gallup survey.
        </Trans>
      </p>

      <H2>
        <Trans id="about.survey-2021-title">2021 UNICEF-Gallup survey</Trans>
      </H2>

      <p>
        <Trans id="about.survey-2021-1">
          For the first phase of Changing Childhood Project, UNICEF and Gallup designed a bespoke
          survey implemented in 21 countries around the world. Drawing on its expertise from
          carrying out its annual World Poll, Gallup surveyed representative samples of at least
          1,000 people in each country (1,500 in India). In each country, the sample was divided
          into two age cohorts: 15- to 24-year-olds (aligned with the United Nations' definition of
          youth), and those aged 40 years and older. Surveys were conducted between February and
          June 2021. All respondents were reached by phone. Developed with the assistance of a range
          of experts, the survey instrument includes questions that had been tested in other polls,
          as well as new ones developed specifically for the Changing Childhood Project.
        </Trans>
      </p>

      <p>
        <Trans id="about.survey-2021-2">
          Given the size of the country samples, and the division of the sample into two age
          cohorts, most of the results derived from this survey data have a margin of error of
          approximately +/- 4 percentage points at the 95% confidence level. Margins of error will
          be larger for smaller subgroups within a population. More information on how to interpret
          the results can be found on the methodology page{' '}
          <A
            download={t({
              id: 'about.methodology-download-filename',
              message: 'unicef-changing-childhood-methodology.pdf',
            })}
            href={methodology2021Url}
          >
            here
          </A>
          .
        </Trans>
      </p>

      <H2>
        <Trans id="about.survey-2023-title">UNICEF + the Gallup World Poll</Trans>
      </H2>

      <p>
        <Trans id="about.survey-2023-1">
          For the second phase of the Changing Childhood Project, UNICEF partnered with Gallup to in
          its <A href={report2023Url}>latest</A> World Poll. We put five of the original 32 Changing
          Childhood Project questions back into the field in 2022-2023 – this time in 55 countries
          (the 21 original countries along with 34 additional ones). The questions focus on three
          areas: the climate crisis, information and trust in them, and identity. All samples are
          probability-based and nationally representative, with sample sizes of approximately 1,000
          per country of those aged 15+. The coverage area is the entire country, including rural
          areas, and the sampling frame represents the entire civilian, non-institutionalized
          population.
        </Trans>
      </p>

      <p>
        <Trans id="about.survey-2023-2">
          Given the size of the country samples, most of the results presented in the microsite have
          a margin of error of approximately ±4 percentage points at the 95 per cent confidence
          level. Margins of error are larger for smaller subgroups within a population. For the
          youth cohort, the median margin of error for the 15–24-year-old cohort across all 55
          countries is +-/7.6 per cent. For further information on survey sampling or margins of
          error, please see the Gallup World Poll methodology summary{' '}
          <A
            download={t({
              id: 'about.methodology-download-filename',
              message: 'unicef-changing-childhood-methodology.pdf',
            })}
            href={methodology2023Url}
          >
            here
          </A>{' '}
          or contact us at the email address below.
        </Trans>
      </p>

      <H2>
        <Trans id="about.learning-more-title">Learn more</Trans>
      </H2>

      <p>
        <Trans id="about.learning-more-2023">
          A more thorough exploration of the Project's findings can be found in the Project's
          reports (2021 and 2023). Users who wish to dig further are encouraged to review the
          questionnaires, methodologies, full microdata and codebooks linked on this page. We would
          love to hear what you discover from your own analyses. If you have questions or would like
          to tell us how you are using the Project, please email us at{' '}
          <A href="mailto:Changing-Childhood@unicef.org">Changing-Childhood@unicef.org</A>.<br />
          <br />
          {/* Previous this had a <Link> wrapped around it but I removed that as I ran into an issue.
              Issue also reported here: https://github.com/aralroca/next-translate/issues/840 */}
          <A href="/">Back to introduction</A>
        </Trans>
      </p>
    </MainContentRoot>
  );
}
